import React, { useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Film, Play } from "lucide-react";
import { ShowreelPlayer, PRESENTER_IMG } from "@/components/showreel/ShowreelPlayer";
import { TID } from "@/lib/testIds";

/** Poster card that opens the full-screen UTSAVA Genesis film. `compact`
 * renders as a single pill button (used in the Dashboard hero). */
export function ShowreelLauncher({ compact = false }) {
    const [open, setOpen] = useState(false);
    const [muted, setMuted] = useState(false);

    return (
        <>
            {compact ? (
                <button
                    data-testid={TID.showreelLauncherBtn}
                    onClick={() => setOpen(true)}
                    className="hud-glass chrome-bevel inline-flex items-center gap-2 px-4 py-2.5 rounded-sm font-mono text-[10px] uppercase tracking-wider text-cyan-300 hover:text-white hover:border-cyan-400/50 transition-colors"
                >
                    <Play size={12} /> Watch the film
                </button>
            ) : (
                <button
                    data-testid={TID.showreelLauncherBtn}
                    onClick={() => setOpen(true)}
                    className="group w-full max-w-md panel backdrop-blur-md bg-[var(--bg-elevated)]/70 p-3 flex items-center gap-4 text-left hover:border-cyan-500/45 transition-colors"
                >
                    <span className="relative w-16 h-16 shrink-0 rounded-sm overflow-hidden border border-white/10 bg-[var(--bg-base)]">
                        <img src={PRESENTER_IMG} alt="" className="w-full h-full object-cover object-top opacity-90" />
                        <span className="absolute inset-0 flex items-center justify-center bg-[#05050A]/45 group-hover:bg-[#05050A]/20 transition-colors">
                            <Play size={16} className="text-cyan-300" />
                        </span>
                    </span>
                    <span className="min-w-0">
                        <span className="label-eyebrow flex items-center gap-1.5"><Film size={11} /> The Genesis Film</span>
                        <span className="block text-sm text-white font-display mt-1">See what UTSAVA Genesis does</span>
                        <span className="block font-mono text-[10px] uppercase tracking-wider text-slate-500 mt-0.5">
                            8 scenes · 90 seconds · narrated
                        </span>
                    </span>
                </button>
            )}

            {createPortal(
                <AnimatePresence>
                    {open && (
                        <motion.div
                            data-testid={TID.showreelOverlay}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.4 }}
                            className="fixed inset-0 z-[100] bg-[var(--bg-base)]"
                        >
                            <ShowreelPlayer muted={muted} setMuted={setMuted} onClose={() => setOpen(false)} />
                        </motion.div>
                    )}
                </AnimatePresence>,
                document.body
            )}
        </>
    );
}
